import {
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  TrendingUp, 
  Users, 
  Building, 
  Percent, 
  ArrowUp,
  CheckCircle, 
  Copy,
  Share2,
  DollarSign,
  Calendar,
  Trophy,
  Zap,
  Target,
  BarChart,
  Mail,
  Instagram,
  X as XIcon,
  Music,
  User,
  Award,
  Clock,
  ShieldCheck,
  Sparkles,
  MessageCircle,
  Briefcase,
  Target as TargetIcon,
  Flame,
  Star,
  Crown,
  Check,
  Shield,
  AlertCircle,
  Loader2
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface AffiliateProgramProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userType?: "creator" | "venue" | "enthusiast";
}

// Secure Activation Form with Email Verification
const ActivationForm = ({ open, onOpenChange }: { open: boolean, onOpenChange: (open: boolean) => void }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    socialInstagram: "",
    socialTwitter: "",
    socialTiktok: "",
    primaryPlatform: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("activate-affiliate", {
        body: formData,
      });

      if (error) throw error;

      if (data?.success) {
        toast.success("Verification email sent!", {
          description: "Please check your inbox and click the verification link to activate your affiliate account."
        });
        onOpenChange(false);
        setFormData({ name: "", email: "", phone: "", socialInstagram: "", socialTwitter: "", socialTiktok: "", primaryPlatform: "" });
      } else {
        throw new Error(data?.error || "Failed to activate");
      }
    } catch (error: any) {
      console.error("Activation error:", error);
      toast.error("Activation failed", {
        description: error.message || "Please try again later"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white text-gray-900 border-gray-200">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-gray-900">
            <Mail className="w-5 h-5" />
            Activate Affiliate Program
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            A verification email will be sent to activate your account
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-6 -mr-6 max-h-[60vh]">
          <form id="activation-form" onSubmit={handleSubmit} className="space-y-4 py-4 px-1">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-900">Full Name *</Label>
              <Input 
                id="name" 
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe" 
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                required
                maxLength={100}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-900">Email Address *</Label>
              <Input 
                id="email" 
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com" 
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                required
                maxLength={255}
              />
              <p className="text-xs text-gray-500">A verification link will be sent to this email</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-900">Phone Number</Label>
              <Input 
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+27 12 345 6789" 
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-900">Social Media Profiles</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-[#e4405f]" />
                  <Input 
                    value={formData.socialInstagram}
                    onChange={(e) => setFormData(prev => ({ ...prev, socialInstagram: e.target.value }))}
                    placeholder="@yourhandle" 
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <XIcon className="w-4 h-4 text-gray-900" />
                  <Input 
                    value={formData.socialTwitter}
                    onChange={(e) => setFormData(prev => ({ ...prev, socialTwitter: e.target.value }))}
                    placeholder="@yourhandle" 
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Music className="w-4 h-4 text-[#ff0050]" />
                  <Input 
                    value={formData.socialTiktok}
                    onChange={(e) => setFormData(prev => ({ ...prev, socialTiktok: e.target.value }))}
                    placeholder="@yourhandle" 
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="platform" className="text-gray-900">Primary Platform</Label>
              <select 
                id="platform"
                value={formData.primaryPlatform}
                onChange={(e) => setFormData(prev => ({ ...prev, primaryPlatform: e.target.value }))}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900"
              >
                <option value="">Select your main platform</option>
                <option value="instagram">Instagram</option>
                <option value="twitter">X (Twitter)</option>
                <option value="tiktok">TikTok</option>
                <option value="youtube">YouTube</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-xs text-blue-800">
                  <strong>Secure Activation:</strong> After submitting, you'll receive an email with a verification link. Your affiliate account will be activated only after email verification.
                </div>
              </div>
            </div>
          </form>
        </ScrollArea>
        
        <DialogFooter className="mt-4">
          <Button 
            type="submit" 
            form="activation-form" 
            className="w-full bg-black hover:bg-gray-900 text-white font-semibold"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending Verification...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Send Verification Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DemoForm = ({ open, onOpenChange }: { open: boolean, onOpenChange: (open: boolean) => void }) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Demo scheduled", { 
      description: "Check your email for confirmation" 
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white text-gray-900 border-gray-200">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-gray-900">
            <Calendar className="w-5 h-5" />
            Schedule a Demo
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Get a personalized walkthrough
          </DialogDescription>
        </DialogHeader>
        
        <form id="demo-form" onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-gray-900">Full Name</Label>
            <Input 
              placeholder="Your name" 
              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-900">Email Address</Label>
            <Input 
              type="email" 
              placeholder="your@email.com" 
              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-900">Phone Number</Label>
            <Input 
              type="tel" 
              placeholder="+27 12 345 6789" 
              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-900">Preferred Date & Time</Label>
            <Input 
              type="datetime-local" 
              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-900">Your Role</Label>
            <select className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900">
              <option value="">Select your role</option>
              <option value="creator">Creator/Artist</option>
              <option value="venue">Venue Owner/Manager</option>
              <option value="influencer">Content Creator</option>
              <option value="agency">Agency/Manager</option>
              <option value="enthusiast">Event Enthusiast</option>
            </select>
          </div>
        </form>
        
        <DialogFooter>
          <Button 
            type="submit" 
            form="demo-form" 
            className="w-full bg-black hover:bg-gray-900 text-white font-semibold"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Demo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AffiliateProgram = ({ open, onOpenChange, userType = "enthusiast" }: AffiliateProgramProps) => {
  const [activeTab, setActiveTab] = useState("structure");
  const [showActivationForm, setShowActivationForm] = useState(false);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [creators, setCreators] = useState(5);
  const [venues, setVenues] = useState(2);
  const [copied, setCopied] = useState(false);
  
  // Show placeholder link - real link only after verification
  const affiliateLink = "Your personal link will be available after email verification";
  
  const copyToClipboard = () => {
    toast.info("Activate your account first", {
      description: "Complete email verification to get your personal affiliate link"
    });
  };

  const commissionStructure = [
    {
      type: "Creator Subscription",
      plan: "R249/month",
      baseRate: "35%",
      bonus: "Up to 45% with performance",
      duration: "First month only",
      icon: <Users className="w-5 h-5 text-[#1d9bf0]" />,
      details: "One-time commission on first month's subscription"
    },
    {
      type: "Venue Subscription",
      plan: "R849/month",
      baseRate: "30%",
      bonus: "Up to 40% with bonuses",
      duration: "First month only",
      icon: <Building className="w-5 h-5 text-[#00ba7c]" />,
      details: "One-time commission on first month's subscription"
    },
    {
      type: "Enterprise Plan",
      plan: "Custom Pricing",
      baseRate: "20%",
      bonus: "Up to 30% + Performance Bonus",
      duration: "First month only",
      icon: <Briefcase className="w-5 h-5 text-[#f91880]" />,
      details: "One-time commission on initial payment"
    },
    {
      type: "Transaction Fees",
      plan: "R2-5 per booking",
      baseRate: "15%",
      bonus: "Volume-based bonuses",
      duration: "First transaction only",
      icon: <DollarSign className="w-5 h-5 text-[#ffd400]" />,
      details: "One-time commission per referred user's first booking"
    }
  ];

  const performanceTiers = [
    {
      tier: "Groove Starter",
      badge: "Beginner",
      requirements: "3 Creators + 1 Venue",
      perks: ["Basic dashboard", "30-day tracking", "Monthly payouts", "Email support"],
      multiplier: "1.0x",
      icon: <Zap className="w-6 h-6 text-[#1d9bf0]" />,
      color: "border-[#1d9bf0]"
    },
    {
      tier: "City Influencer",
      badge: "Pro",
      requirements: "15 Creators + 5 Venues",
      perks: ["Advanced analytics", "90-day tracking", "Weekly payouts", "Priority support"],
      multiplier: "1.05x",
      icon: <Award className="w-6 h-6 text-[#00ba7c]" />,
      color: "border-[#00ba7c]"
    },
    {
      tier: "Metro Ambassador",
      badge: "Elite",
      requirements: "50 Creators + 15 Venues",
      perks: ["VIP dashboard", "365-day tracking", "3-day payouts", "Dedicated manager"],
      multiplier: "1.15x",
      icon: <Crown className="w-6 h-6 text-[#f91880]" />,
      color: "border-[#f91880]"
    },
  ];

  const shareOptions = [
    { name: "WhatsApp", icon: <MessageCircle className="w-6 h-6" />, color: "bg-[#25d366] hover:bg-[#22c45e]" },
    { name: "Instagram", icon: <Instagram className="w-6 h-6" />, color: "bg-[#e4405f] hover:bg-[#d6336c]" },
    { name: "Email", icon: <Mail className="w-6 h-6" />, color: "bg-[#1d9bf0] hover:bg-[#1a8cd8]" },
    { name: "X", icon: <XIcon className="w-6 h-6" />, color: "bg-black hover:bg-gray-900 border border-[#2f3336]" },
    { name: "TikTok", icon: <Music className="w-6 h-6" />, color: "bg-[#ff0050] hover:bg-[#e60046]" },
    { name: "Copy", icon: copied ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />, color: "bg-[#f91880] hover:bg-[#e01672]" }
  ];

  const calculateEarnings = () => {
    const creatorRevenue = creators * 249 * 0.35;
    const venueRevenue = venues * 849 * 0.30;
    const total = creatorRevenue + venueRevenue;
    
    let multiplier = 1.0;
    if (creators >= 50 && venues >= 15) multiplier = 1.15;
    else if (creators >= 15 && venues >= 5) multiplier = 1.05;
    
    return {
      oneTime: (total * multiplier).toFixed(2),
      creatorRevenue: creatorRevenue.toFixed(2),
      venueRevenue: venueRevenue.toFixed(2),
      multiplier: multiplier
    };
  };

  const earnings = calculateEarnings();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="p-0 w-screen h-screen max-w-full sm:max-w-4xl sm:h-[90vh] flex flex-col bg-white text-gray-900 border-gray-200">
          <DialogHeader className="p-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-600 to-pink-500 rounded-xl text-white">
                  <TrendingUp className="w-7 h-7" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold text-gray-900">
                    Affiliate Program
                  </DialogTitle>
                  <DialogDescription className="text-gray-500">
                    Earn commissions building creative economy
                  </DialogDescription>
                </div>
              </div>
              <div className="px-4 py-2 bg-gray-100 rounded-full border border-gray-200">
                <span className="text-sm font-semibold flex items-center gap-2 text-gray-700">
                  <ShieldCheck className="w-4 h-4" />
                  {userType.toUpperCase()} RATES
                </span>
              </div>
            </div>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex-1 flex flex-col min-h-0">
            <div className="px-6 pt-4">
              <TabsList className="grid grid-cols-5 bg-gray-100 rounded-lg p-1 border border-gray-200">
                <TabsTrigger value="structure" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-md text-xs sm:text-sm text-gray-600">
                  <Percent className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Earnings</span>
                </TabsTrigger>
                <TabsTrigger value="tiers" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-md text-xs sm:text-sm text-gray-600">
                  <Trophy className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Tiers</span>
                </TabsTrigger>
                <TabsTrigger value="calculator" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-md text-xs sm:text-sm text-gray-600">
                  <BarChart className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Calculator</span>
                </TabsTrigger>
                <TabsTrigger value="share" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-md text-xs sm:text-sm text-gray-600">
                  <Share2 className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Share</span>
                </TabsTrigger>
                <TabsTrigger value="terms" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-md text-xs sm:text-sm text-gray-600">
                  <Shield className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Terms</span>
                </TabsTrigger>
              </TabsList>
            </div>
            
            <div className="flex-1 min-h-0">
              <ScrollArea className="h-full">
                <div className="px-6 pt-6 pb-8">
                  {/* Commission Structure Tab */}
                  <TabsContent value="structure" className="space-y-6">
                    <h3 className="text-lg font-bold mb-4 text-gray-900">Commission Structure</h3>
                    <div className="grid gap-4">
                      {commissionStructure.map((item, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              {item.icon}
                              <div>
                                <h4 className="font-bold text-gray-900">{item.type}</h4>
                                <p className="text-sm text-gray-500">{item.plan}</p>
                                <p className="text-xs text-gray-500 mt-1">{item.details}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-green-600">{item.baseRate}</div>
                              <div className="text-sm text-gray-500">base rate</div>
                              <div className="text-xs text-green-600 mt-1 flex items-center">
                                <ArrowUp className="w-3 h-3 mr-1" />
                                {item.bonus}
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 flex items-center justify-between text-sm">
                            <span className="text-gray-500">Duration</span>
                            <span className="bg-gray-200 px-3 py-1 rounded-full font-medium text-gray-700">{item.duration}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                      <h3 className="font-bold mb-3 flex items-center gap-2 text-gray-900">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Special Benefits
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-white rounded-lg border border-gray-200 text-black">
                          <div className="flex items-center gap-2 mb-1">
                            <TargetIcon className="w-4 h-4 text-pink-500" />
                            <div className="text-sm font-medium text-gray-900">Fast Start Bonus</div>
                          </div>
                          <div className="text-xs text-gray-500">R1,000 for first 5 referrals</div>
                        </div>
                        <div className="p-3 bg-white rounded-lg border border-gray-200 text-black">
                          <div className="flex items-center gap-2 mb-1">
                            <Users className="w-4 h-4 text-blue-500" />
                            <div className="text-sm font-medium text-gray-900">30% Referral Discount</div>
                          </div>
                          <div className="text-xs text-gray-500">Your referrals get 30% off for 1 year while link is active</div>
                        </div>
                      </div>
                      <div className="mt-3 p-3 bg-pink-50 rounded-lg border border-pink-200">
                        <div className="flex items-center gap-2 text-xs text-pink-600">
                          <AlertCircle className="w-4 h-4" />
                          <span><strong>Important:</strong> Referrals receive 30% discount for 1 year while your affiliate link remains active. Commissions are paid on first month only.</span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Tiers Tab */}
                  <TabsContent value="tiers" className="space-y-6">
                    <h3 className="text-lg font-bold mb-6 text-center text-gray-900">Performance Tiers</h3>
                    <div className="space-y-4">
                      {performanceTiers.map((tier) => (
                        <div key={tier.tier} className={`p-5 rounded-lg border-2 ${tier.color} bg-white`}>
                          <div className="flex items-start justify-between mb-5">
                            <div className="flex items-center gap-3">
                              {tier.icon}
                              <div>
                                <h4 className="font-bold text-lg text-gray-900">{tier.tier}</h4>
                                <div className="inline-flex items-center px-3 py-1 mt-2 text-xs font-bold bg-gray-100 rounded-full text-gray-700">
                                  {tier.badge}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-900">{tier.multiplier}</div>
                              <div className="text-sm text-gray-500">Multiplier</div>
                            </div>
                          </div>
                          
                          <div className="mb-5">
                            <div className="text-sm font-medium text-gray-500 mb-2">Requirements</div>
                            <div className="text-base font-semibold bg-gray-100 px-4 py-3 rounded-lg border border-gray-200 text-gray-900">
                              {tier.requirements}
                            </div>
                          </div>
                          
                          <div>
                            <div className="text-sm font-medium text-gray-500 mb-3">Exclusive Perks</div>
                            <ul className="space-y-2">
                              {tier.perks.map((perk, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  {perk}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-5 bg-gradient-to-r from-pink-50 to-yellow-50 rounded-lg border border-pink-200">
                      <h3 className="font-bold mb-3 flex items-center gap-2 text-gray-900">
                        <Flame className="w-5 h-5 text-pink-500" />
                        Fast Start Challenge
                      </h3>
                      <p className="text-gray-700 mb-4">
                        Refer 5 creators + 2 venues in your first 30 days for a <strong className="text-green-600">R1,000 bonus</strong>
                      </p>
                      <div className="bg-white px-4 py-3 rounded-lg border border-gray-200 text-black">
                        <div className="text-sm font-bold flex items-center justify-between text-gray-700">
                          <div className="flex items-center gap-2">
                            <TargetIcon className="w-4 h-4" />
                            <span>5 Creators + 2 Venues</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4" />
                            <span className="text-green-600">R1,000 Bonus</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>30 days</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Calculator Tab */}
                  <TabsContent value="calculator" className="space-y-6">
                    <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-gray-900">
                        <BarChart className="w-5 h-5 text-purple-600" />
                        Earnings Calculator
                      </h3>
                      
                      <div className="space-y-6">
                        <div>
                          <Label className="text-sm font-medium mb-4 block text-gray-900">Creators Referred: {creators}</Label>
                          <Input 
                            type="range" 
                            min="0" 
                            max="100" 
                            value={creators}
                            onChange={(e) => setCreators(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-full"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-2">
                            <span>0</span>
                            <span>25</span>
                            <span>50</span>
                            <span>75</span>
                            <span>100</span>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium mb-4 block text-gray-900">Venues Referred: {venues}</Label>
                          <Input 
                            type="range" 
                            min="0" 
                            max="50" 
                            value={venues}
                            onChange={(e) => setVenues(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-full"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-2">
                            <span>0</span>
                            <span>12</span>
                            <span>25</span>
                            <span>37</span>
                            <span>50</span>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <div className="text-sm text-gray-500">One-Time Earnings (First Month Only)</div>
                            <div className="text-3xl font-bold text-green-600">R{earnings.oneTime}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              Tier: {creators >= 50 ? "Metro Ambassador" : creators >= 15 ? "City Influencer" : "Groove Starter"} ({earnings.multiplier}x)
                            </div>
                          </div>
                          <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
                            <div className="flex items-center gap-2 text-xs text-pink-600">
                              <AlertCircle className="w-4 h-4" />
                              <span>Commissions are paid once per referral on their first month's subscription only.</span>
                            </div>
                          </div>
                        </div>

                        <Button 
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                          onClick={() => {
                            toast.success(`Estimated one-time earnings: R${earnings.oneTime}`, {
                              description: "Based on your referrals' first month subscriptions"
                            });
                          }}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Calculate Earnings
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Share Tab */}
                  <TabsContent value="share" className="space-y-6">
                    <div>
                      <h3 className="text-lg font-bold mb-6 text-gray-900">Share & Earn</h3>
                      
                      {/* Secure notice */}
                      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start gap-3">
                          <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-blue-700 mb-1">Secure Referral System</h4>
                            <p className="text-sm text-gray-700">
                              Your personal referral link will be generated after email verification. Referrals who sign up as Creator or Venue using your link receive <strong>30% off for 1 year</strong> while your link remains active!
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Affiliate Link */}
                      <div className="mb-6">
                        <Label className="text-sm font-medium mb-2 text-gray-900">Your Personal Affiliate Link</Label>
                        <div className="flex gap-2">
                          <div className="flex-1 p-3 bg-gray-100 rounded-lg border border-gray-200 truncate text-sm text-gray-500 italic">
                            {affiliateLink}
                          </div>
                          <Button 
                            variant="outline"
                            onClick={copyToClipboard}
                            className="border-gray-300 hover:bg-gray-100"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Complete email verification to get your unique referral link
                        </p>
                      </div>

                      {/* CTA to activate */}
                      <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <h4 className="font-bold mb-2 flex items-center gap-2 text-gray-900">
                          <Users className="w-4 h-4 text-green-600" />
                          Referral Benefits
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            Your referrals get <strong>30% off for 1 year</strong> while link is active (Creator/Venue)
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            You earn commission on their first subscription payment
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            Secure tracking via unique referral codes
                          </li>
                        </ul>
                      </div>

                      {/* Share Templates */}
                      <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="font-bold mb-3 text-gray-900">Share Templates</h4>
                        <div className="space-y-3">
                          <div className="p-3 bg-white rounded-lg border border-gray-200 text-black">
                            <div className="text-sm font-medium mb-1 text-gray-900 flex items-center gap-2">
                              <Users className="w-4 h-4 text-blue-500" />
                              For Creators
                            </div>
                            <p className="text-xs text-gray-500">"Join Metro City Groove with my link and get 30% off for a full year! The platform professionalizing SA's creative economy."</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg border border-gray-200 text-black">
                            <div className="text-sm font-medium mb-1 text-gray-900 flex items-center gap-2">
                              <Building className="w-4 h-4 text-green-500" />
                              For Venues
                            </div>
                            <p className="text-xs text-gray-500">"Use my referral link for 30% off for 1 year on Metro City Groove. Discover amazing local talent and manage events seamlessly."</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Terms & Conditions Tab */}
                  <TabsContent value="terms" className="space-y-6">
                    <div>
                      <h3 className="text-lg font-bold mb-2 text-gray-900">Affiliate Program Terms & Conditions</h3>
                      <p className="text-sm text-gray-500 mb-6">Last updated: January 2026</p>
                      
                      {/* Important Notice */}
                      <div className="p-4 bg-pink-50 rounded-lg border border-pink-200 mb-6">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-pink-700 mb-1">Important: 1-Year Discount Policy</h4>
                            <p className="text-sm text-gray-700">
                              Referred users receive <strong>30% off for 1 year</strong> while the affiliate link remains active. Affiliate commissions are paid on a <strong>one-time basis only</strong> on the first month's payment.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Terms Sections */}
                      <div className="space-y-6">
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                            Commission Structure
                          </h4>
                          <div className="space-y-2 text-sm text-gray-500">
                            <p>1.1. <strong className="text-gray-900">One-Time Payment:</strong> Affiliates earn a one-time commission based on the first month's subscription payment made by each referred user.</p>
                            <p>1.2. <strong className="text-gray-900">Commission Rates:</strong> Creator: 35% (R249). Venue: 30% (R849). Enterprise: 20%.</p>
                            <p>1.3. <strong className="text-gray-900">Referral Discount:</strong> Referred users signing up as Creator or Venue receive 30% off for 1 year while the affiliate link is active.</p>
                          </div>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                            Account Activation
                          </h4>
                          <div className="space-y-2 text-sm text-gray-500">
                            <p>2.1. <strong className="text-gray-900">Email Verification:</strong> Affiliate accounts require email verification before activation.</p>
                            <p>2.2. <strong className="text-gray-900">Unique Referral Code:</strong> Each verified affiliate receives a unique referral code for tracking.</p>
                            <p>2.3. <strong className="text-gray-900">Secure Links:</strong> Personal referral links are generated only after verification.</p>
                          </div>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-pink-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                            Payment Terms
                          </h4>
                          <div className="space-y-2 text-sm text-gray-500">
                            <p>3.1. <strong className="text-gray-900">Minimum Payout:</strong> R500 minimum balance required for withdrawal.</p>
                            <p>3.2. <strong className="text-gray-900">Payment Schedule:</strong> Monthly payouts for Starter tier, weekly for Pro, 3-day for Elite.</p>
                            <p>3.3. <strong className="text-gray-900">Verification:</strong> Commission eligibility verified after referred user's first payment.</p>
                          </div>
                        </div>

                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-start gap-3">
                            <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <h4 className="font-bold text-gray-900 mb-1">Acceptance of Terms</h4>
                              <p className="text-sm text-gray-500">
                                By activating your affiliate account, you acknowledge that you have read, understood, and agree to be bound by these terms. Governed by South African law.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </div>
              </ScrollArea>
            </div>
          </Tabs>
          
          <div className="p-6 border-t border-gray-200 space-y-3">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span><strong>Note:</strong> Demo scheduling requires an active Personal Affiliate Link</span>
              </p>
            </div>
            <Button 
              variant="outline" 
              className="w-full border-gray-300 hover:bg-gray-100 text-gray-700"
              onClick={() => setShowDemoForm(true)}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Demo (Requires Active Link)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <ActivationForm open={showActivationForm} onOpenChange={setShowActivationForm} />
      <DemoForm open={showDemoForm} onOpenChange={setShowDemoForm} />
    </>
  );
};

export default AffiliateProgram;
